import React, { useState, useEffect } from 'react';
import { UserManagement as UserManagementType, adminAPI } from '../../services/api';
import './UserManagement.css';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserManagementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{userId: string, action: string, userName: string} | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        
        // Fetch real data from API
        const response = await adminAPI.getUsers({
          status: filterStatus === 'all' ? undefined : filterStatus,
          search: search || undefined,
          page: currentPage,
          limit: 10
        });
        
        if (response.success) {
          setUsers(response.data.users);
          setTotalPages(response.data.pagination.pages);
          setTotalUsers(response.data.pagination.total);
          setError(null);
        } else {
          setError(response.message || 'Failed to fetch users');
        }
      } catch (err) {
        setError('Failed to fetch users');
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [filterStatus, currentPage, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const filteredUsers = users.filter(user => {
    if (verificationFilter === 'verified') return user.nsfasVerified;
    if (verificationFilter === 'pending') return !user.nsfasVerified;
    return true;
  });

  const visibleCount = verificationFilter === 'all' ? totalUsers : filteredUsers.length;

  // Utility functions - exactly like PaymentTracking
  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString('en-ZA', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric'
  //   });
  // };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const loanLabel = (count: number) => `${count} ${count === 1 ? 'loan' : 'loans'}`;

  // Handle user actions
  const handleUserAction = async (userId: string, action: string, userName: string) => {
    try {
      setActionLoading(userId);
      
      const response = await adminAPI.updateUserStatus(userId, action as 'suspend' | 'activate' | 'delete');
      
      if (response.success) {
        // Refresh the users list
        const usersResponse = await adminAPI.getUsers({
          page: currentPage,
          limit: 10,
          search: search || undefined,
          status: filterStatus === 'all' ? undefined : filterStatus
        });
        
        if (usersResponse.success) {
          setUsers(usersResponse.data.users);
          setTotalPages(usersResponse.data.pagination.pages);
          setTotalUsers(usersResponse.data.pagination.total);
        }
        
        setConfirmAction(null);
        // You could add a success toast notification here
        console.log(`User ${action}d successfully`);
      } else {
        setError(response.message || `Failed to ${action} user`);
      }
    } catch (err) {
      setError(`Failed to ${action} user`);
      console.error(`Error ${action}ing user:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  const showConfirmDialog = (userId: string, action: string, userName: string) => {
    setConfirmAction({ userId, action, userName });
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      {/* Header - exactly like PaymentTracking */}
      <div className="user-header">
        <h2 className="user-title">User Management</h2>
        <p className="user-subtitle">
          Manage registered users, monitor accounts, and track user activity
        </p>
      </div>

      {/* Filters - exactly like PaymentTracking */}
      <div className="user-controls">
        <label className="user-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search users by name, email, or ID..."
            aria-label="Search users"
          />
        </label>
        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="deleted">Deleted</option>
        </select>
        <select
          className="filter-select"
          value={verificationFilter}
          onChange={(e) => setVerificationFilter(e.target.value)}
          aria-label="Filter by verification"
        >
          <option value="all">All verification</option>
          <option value="verified">Verified NSFAS</option>
          <option value="pending">Pending NSFAS</option>
        </select>
        <span className="results-count">{visibleCount} users</span>
      </div>

      {/* Users Table - exactly like PaymentTracking */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Contact</th>
              <th>Institution</th>
              <th>Status</th>
              <th>Verification</th>
              <th>Loans</th>
              <th>Repayment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className="user-row">
                <td>
                  <div className="user-info">
                    <div className="user-avatar">
                      {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                    </div>
                    <div className="user-details">
                      <span className="user-name">{user.firstName} {user.lastName}</span>
                      <span className="user-id" title={user.id}>{user.id.slice(0, 13)}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="contact-info">
                    <span className="user-email">{user.email}</span>
                    <span className="user-phone">{user.phone}</span>
                  </div>
                </td>
                <td>
                  <div className="institution-info">
                    <span className="institution-name">{user.institution || '—'}</span>
                    <span className="student-number">{user.studentNumber || '—'}</span>
                  </div>
                </td>
                <td>
                  <span className={`status-badge status-badge--${user.status}`}>
                    <span className="status-dot" aria-hidden="true" />
                    {user.status}
                  </span>
                </td>
                <td>
                  <span className={`verification-badge ${user.nsfasVerified ? 'verified' : 'pending'}`}>
                    {user.nsfasVerified ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <polyline points="8 12.5 11 15.5 16 9.5" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                      </svg>
                    )}
                    {user.nsfasVerified ? 'Verified · NSFAS' : 'Pending · NSFAS'}
                  </span>
                </td>
                <td>
                  <div className="loans-info">
                    <span className="total-loans">{loanLabel(user.totalLoans)}</span>
                    <span className="borrowed-amount">Borrowed: {formatCurrency(user.totalBorrowed)}</span>
                  </div>
                </td>
                <td>
                  <span className="repayment-amount">{formatCurrency(user.totalRepaid)}</span>
                </td>
                <td>
                  <div className="user-actions">
                    {user.status === 'active' && (
                      <button
                        type="button"
                        className="user-action user-action--warn"
                        onClick={() => showConfirmDialog(user.id, 'suspend', `${user.firstName} ${user.lastName}`)}
                        disabled={actionLoading === user.id}
                      >
                        Suspend
                      </button>
                    )}
                    {user.status === 'suspended' && (
                      <button
                        type="button"
                        className="user-action user-action--restore"
                        onClick={() => showConfirmDialog(user.id, 'activate', `${user.firstName} ${user.lastName}`)}
                        disabled={actionLoading === user.id}
                      >
                        Activate
                      </button>
                    )}
                    {user.status === 'deleted' && (
                      <button
                        type="button"
                        className="user-action user-action--restore"
                        onClick={() => showConfirmDialog(user.id, 'activate', `${user.firstName} ${user.lastName}`)}
                        disabled={actionLoading === user.id}
                      >
                        Restore
                      </button>
                    )}
                    <button
                      type="button"
                      className="user-action user-action--danger"
                      onClick={() => showConfirmDialog(user.id, 'delete', `${user.firstName} ${user.lastName}`)}
                      disabled={actionLoading === user.id}
                    >
                      {user.status === 'deleted' ? 'Delete permanently' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#333' }}>
              Confirm Action
            </h3>
            <p style={{ marginBottom: '2rem', color: '#666' }}>
              Are you sure you want to <strong>{confirmAction.action}</strong> user <strong>{confirmAction.userName}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#64748B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleUserAction(confirmAction.userId, confirmAction.action, confirmAction.userName)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: confirmAction.action === 'delete' ? '#FF4136' : confirmAction.action === 'suspend' ? '#EA580C' : '#2ECC40',
                  color: confirmAction.action === 'suspend' ? '#000' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {confirmAction.action === 'suspend' ? 'Suspend' : confirmAction.action === 'activate' ? 'Activate' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;